import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import {
  BookOpen,
  Search,
  Clock,
  Users,
  Star,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Filter,
  GraduationCap,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { catalogAPI } from '../lib/api';
import { toast } from 'sonner';

const categories = [
  'All Categories',
  'Technology',
  'Business',
  'Leadership',
  'Communication',
  'Design',
  'Marketing',
  'Finance',
  'Data Science',
  'Human Resources',
  'Personal Development',
  'Sales',
  'Customer Service',
  'Healthcare',
  'Manufacturing',
  'Compliance & Legal',
];

const difficulties = [
  { value: 'all', label: 'Professional Level' },
  { value: 'Professional', label: 'Professional Certificate' },
];

const ITEMS_PER_PAGE = 20;

export default function CourseCatalogPage() {
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All Categories');
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCourses, setTotalCourses] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [stats, setStats] = useState(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, selectedDifficulty]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await catalogAPI.getStats();
        setStats(response.data);
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      }
    };
    fetchStats();
  }, []);

  useEffect(() => {
    fetchCourses();
  }, [debouncedSearch, selectedCategory, selectedDifficulty, currentPage]);

  const fetchCourses = async () => {
    setLoading(true);
    try {
      const params = {
        q: debouncedSearch || '',
        limit: ITEMS_PER_PAGE,
        skip: (currentPage - 1) * ITEMS_PER_PAGE,
      };
      
      if (selectedCategory !== 'All Categories') {
        params.category = selectedCategory;
      }
      
      if (selectedDifficulty !== 'all') {
        params.difficulty = selectedDifficulty;
      }

      const response = await catalogAPI.search(params);
      setCourses(response.data.courses);
      setTotalCourses(response.data.total);
      setTotalPages(response.data.pages);
    } catch (error) {
      toast.error('Failed to load courses');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const showPages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(showPages / 2));
    let endPage = Math.min(totalPages, startPage + showPages - 1);

    if (endPage - startPage + 1 < showPages) {
      startPage = Math.max(1, endPage - showPages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="flex items-center justify-center gap-2 mt-10" data-testid="pagination">
        <Button
          variant="outline"
          size="sm"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="border border-slate-200 hover:bg-slate-50"
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>

        {startPage > 1 && (
          <>
            <Button
              variant={currentPage === 1 ? 'default' : 'outline'}
              size="sm"
              onClick={() => handlePageChange(1)}
              className={currentPage === 1 ? 'bg-[#0056D2]' : 'border border-slate-200'}
            >
              1
            </Button>
            {startPage > 2 && <span className="px-2 text-slate-400">...</span>}
          </>
        )}

        {pages.map((page) => (
          <Button
            key={page}
            variant={currentPage === page ? 'default' : 'outline'}
            size="sm"
            onClick={() => handlePageChange(page)}
            className={currentPage === page ? 'bg-[#0056D2]' : 'border border-slate-200 hover:bg-slate-50'}
          >
            {page}
          </Button>
        ))}

        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span className="px-2 text-slate-400">...</span>}
            <Button
              variant={currentPage === totalPages ? 'default' : 'outline'}
              size="sm"
              onClick={() => handlePageChange(totalPages)}
              className={currentPage === totalPages ? 'bg-[#0056D2]' : 'border border-slate-200'}
            >
              {totalPages}
            </Button>
          </>
        )}

        <Button
          variant="outline"
          size="sm"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="border border-slate-200 hover:bg-slate-50"
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    );
  };

  return (
    <Layout>
      <div className="space-y-8" data-testid="course-catalog">
        {/* Hero Banner */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-2xl p-8 text-white">
          <div className="max-w-2xl">
            <h1 className="font-heading text-3xl md:text-4xl font-bold mb-3">Explore Professional Courses</h1>
            <p className="text-blue-100 text-lg mb-6">
              {stats ? `${stats.total_courses.toLocaleString()}+ professional courses` : 'Thousands of courses'} to advance your career
            </p>
            <div className="relative max-w-xl">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="text"
                placeholder="Search for courses, skills, or topics..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 pr-4 py-3 h-12 rounded-xl border-0 text-slate-900 bg-white w-full shadow-lg"
                data-testid="search-input"
              />
            </div>
          </div>
        </div>

        {/* Filters Bar */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-100">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Filter className="w-4 h-4" />
              <span className="font-medium">Filter by:</span>
            </div>
            
            <div className="flex flex-wrap gap-3 flex-1">
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-48 border-slate-200 bg-slate-50 hover:bg-slate-100" data-testid="category-filter">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={selectedDifficulty} onValueChange={setSelectedDifficulty}>
                <SelectTrigger className="w-40 border-slate-200 bg-slate-50 hover:bg-slate-100" data-testid="difficulty-filter">
                  <SelectValue placeholder="Level" />
                </SelectTrigger>
                <SelectContent>
                  {difficulties.map((diff) => (
                    <SelectItem key={diff.value} value={diff.value}>
                      {diff.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {(searchQuery || selectedCategory !== 'All Categories' || selectedDifficulty !== 'all') && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory('All Categories');
                  setSelectedDifficulty('all');
                }}
                className="text-[#0056D2] hover:text-[#00419E] hover:bg-blue-50"
              >
                Clear all
              </Button>
            )}
          </div>
        </div>

        {/* Results Count */}
        <div className="flex items-center justify-between px-1">
          <p className="text-slate-600 text-sm">
            {loading ? (
              <span className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading courses...
              </span>
            ) : (
              <>
                <span className="font-semibold text-slate-900">{totalCourses.toLocaleString()}</span> results
                {totalPages > 1 && (
                  <span className="text-slate-400 ml-2">
                    • Page {currentPage} of {totalPages}
                  </span>
                )}
              </>
            )}
          </p>
        </div>

        {/* Course Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-[#0056D2]" />
          </div>
        ) : courses.length > 0 ? (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {courses.map((course, idx) => (
                <CourseCard key={course.id} course={course} index={idx} />
              ))}
            </div>
            {renderPagination()}
          </>
        ) : (
          <div className="bg-white rounded-xl text-center py-16 shadow-sm border border-slate-100">
            <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="font-heading text-xl text-slate-900 mb-2">No courses found</h3>
            <p className="text-slate-500 mb-6">
              Try adjusting your filters or search query
            </p>
            <Button
              onClick={() => {
                setSearchQuery('');
                setSelectedCategory('All Categories');
                setSelectedDifficulty('all');
              }}
              className="bg-[#0056D2] hover:bg-[#00419E]"
            >
              Clear Filters
            </Button>
          </div>
        )}
      </div>
    </Layout>
  );
}

function CourseCard({ course, index }) {
  // Course thumbnails based on category
  const getThumbnail = () => {
    const thumbnails = {
      'Technology': 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=400&h=200&fit=crop',
      'Business': 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=200&fit=crop',
      'Data Science': 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=200&fit=crop',
      'Leadership': 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=200&fit=crop',
      'Marketing': 'https://images.unsplash.com/photo-1533750349088-cd871a92f312?w=400&h=200&fit=crop',
      'Finance': 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=400&h=200&fit=crop',
      'Design': 'https://images.unsplash.com/photo-1561070791-2526d30994b5?w=400&h=200&fit=crop',
      'Personal Development': 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=400&h=200&fit=crop',
    };
    return course.thumbnail_url || thumbnails[course.category] || thumbnails['Technology'];
  };

  return (
    <Link to={`/courses/${course.id}`} className="block group">
      <div 
        className="course-card animate-fade-in" 
        style={{ animationDelay: `${index * 50}ms` }}
        data-testid={`course-card-${course.id}`}
      >
        {/* Thumbnail */}
        <div className="relative aspect-video overflow-hidden">
          <img
            src={getThumbnail()}
            alt={course.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          <div className="absolute bottom-3 left-3 right-3">
            <span className="inline-flex items-center px-2 py-1 rounded bg-white/90 text-xs font-medium text-slate-700">
              <GraduationCap className="w-3 h-3 mr-1" />
              Professional Certificate
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 flex flex-col flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-medium text-[#0056D2] bg-blue-50 px-2 py-0.5 rounded">
              {course.category}
            </span>
          </div>
          
          <h3 className="font-semibold text-slate-900 text-sm mb-1 line-clamp-2 group-hover:text-[#0056D2] transition-colors">
            {course.title}
          </h3>
          
          <p className="text-xs text-slate-500 mb-3">{course.provider || 'SketchLearn'}</p>

          {/* Rating & Stats */}
          <div className="mt-auto pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
            <div className="flex items-center gap-1">
              <Star className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />
              <span className="font-medium text-slate-700">{course.rating?.toFixed(1) || '4.7'}</span>
              <span className="text-slate-400">({(course.enrolled_count || Math.floor(Math.random() * 5000) + 1000).toLocaleString()})</span>
            </div>
            <div className="flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              <span>{course.duration_hours || 8}h</span>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}
